/**
 * History Page
 * 
 * 폴더 트리 구조의 히스토리 관리 페이지
 * - 왼쪽: 폴더 트리 + 히스토리 목록
 * - 오른쪽: 선택한 평가 상세
 * - 스플릿 뷰 (드래그로 크기 조절 가능)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage, parseUTCDate } from '../i18n';
import { useAuth } from '../auth';
import { stripBullet } from '../utils';
import {
  getHistoryTabs,
  getHistory,
  getHistoryDetail,
  getFoldersTree,
  createFolder,
  updateFolder,
  deleteFolder,
  updateHistory,
  downloadHistoryPdf,
  downloadTestcaseFile,
  downloadPlanningFile,
  downloadAllFilesZip,
  getQuotaInfo,
  type HistoryItem,
  type HistoryTabInfo,
  type FolderInfo,
  type QuotaInfo,
  type HistoryFilters,
  type HistoryDetailResponse,
} from '../api';
import type { ReleaseStatus, ReleaseInput, StructuredRecommendation, RecommendationPriority, SuggestedTestCase, TestCaseStatus } from '../types';
import GitHubReportBlock, { type GithubContextForReport } from './GitHubReportBlock';
import TriRadarChart from './TriRadarChart';

// AI 제안 테스트 케이스 결과 컴포넌트
function SuggestedTestcasesResult({ testcases, lang }: { testcases: SuggestedTestCase[]; lang: 'ko' | 'en' }) {
  const statusConfig: Record<TestCaseStatus, { icon: string; color: string; label: string }> = {
    'pass': { icon: '✓', color: 'text-status-go', label: lang === 'ko' ? '통과' : 'Pass' },
    'fail': { icon: '✗', color: 'text-status-danger', label: lang === 'ko' ? '실패' : 'Fail' },
    'n/a': { icon: '−', color: 'text-terminal-muted', label: lang === 'ko' ? '해당없음' : 'N/A' },
    'n/t': { icon: '○', color: 'text-status-warning', label: lang === 'ko' ? '미테스트' : 'Not Tested' },
  };

  const priorityConfig: Record<string, { color: string; label: string }> = {
    'high': { color: 'text-status-danger', label: lang === 'ko' ? '높음' : 'High' },
    'medium': { color: 'text-status-warning', label: lang === 'ko' ? '보통' : 'Medium' },
    'low': { color: 'text-terminal-muted', label: lang === 'ko' ? '낮음' : 'Low' },
  };

  const passCount = testcases.filter(tc => tc.status === 'pass').length;
  const failCount = testcases.filter(tc => tc.status === 'fail').length;
  const ntCount = testcases.filter(tc => tc.status === 'n/t').length;
  const naCount = testcases.filter(tc => tc.status === 'n/a').length;

  return (
    <div className="card p-4">
      <div className="text-terminal-muted text-xs mb-3 uppercase tracking-wide flex items-center gap-2">
        <span>💡</span>
        <span># {lang === 'ko' ? 'AI 제안 테스트 케이스' : 'AI Suggested Test Cases'}</span>
      </div>
      
      {/* 요약 */}
      <div className="flex flex-wrap gap-3 mb-3 text-xs">
        <span className="text-status-go">✓ {passCount} {lang === 'ko' ? '통과' : 'Pass'}</span>
        <span className="text-status-danger">✗ {failCount} {lang === 'ko' ? '실패' : 'Fail'}</span>
        <span className="text-status-warning">○ {ntCount} {lang === 'ko' ? '미테스트' : 'Not Tested'}</span>
        <span className="text-terminal-muted">− {naCount} {lang === 'ko' ? '해당없음' : 'N/A'}</span>
      </div>

      {/* 테스트 케이스 목록 */}
      <div className="space-y-2">
        {testcases.map((tc) => {
          const status = statusConfig[tc.status];
          const priority = priorityConfig[tc.priority] || priorityConfig['medium'];
          
          return (
            <div key={tc.id} className="bg-terminal-dim/20 p-2 rounded border border-terminal-border">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs mb-1">
                    <span className="text-terminal-muted font-mono">{tc.id}</span>
                    <span className="text-terminal-dim">|</span>
                    <span className="text-terminal-muted">{tc.category}</span>
                    <span className="text-terminal-dim">|</span>
                    <span className={priority.color}>{priority.label}</span>
                  </div>
                  <p className="text-sm text-terminal-text">{tc.title || tc.description}</p>
                  {tc.title && tc.description && tc.title !== tc.description && (
                    <p className="text-xs text-terminal-muted mt-1">{tc.description}</p>
                  )}
                  
                  {/* GitHub 커밋 정보 표시 */}
                  {tc.github_commit_sha && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px]">
                      <span className="px-1.5 py-0.5 bg-purple-500/10 border border-purple-500/30 rounded text-purple-400 font-mono">
                        🔗 {lang === 'ko' ? '커밋' : 'Commit'}: {tc.github_commit_sha.substring(0, 7)}
                      </span>
                      {tc.github_repo && (
                        <span className="px-1.5 py-0.5 bg-blue-500/10 border border-blue-500/30 rounded text-blue-400">
                          📦 {tc.github_repo}
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* 담당자 정보 표시 */}
                  {(tc.author || tc.tester || tc.modifier) && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px]">
                      {tc.author && (
                        <span className="px-1.5 py-0.5 bg-cyan-500/10 border border-cyan-500/30 rounded text-cyan-400">
                          👤 {lang === 'ko' ? '작성자' : 'Author'}: {tc.author}
                        </span>
                      )}
                      {tc.tester && (
                        <span className="px-1.5 py-0.5 bg-green-500/10 border border-green-500/30 rounded text-green-400">
                          🧪 {lang === 'ko' ? '진행자' : 'Tester'}: {tc.tester}
                        </span>
                      )}
                      {tc.modifier && (
                        <span className="px-1.5 py-0.5 bg-orange-500/10 border border-orange-500/30 rounded text-orange-400">
                          ✏️ {lang === 'ko' ? '수정자' : 'Modifier'}: {tc.modifier}
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* 메모 표시 */}
                  {tc.memo && (
                    <div className="mt-2 p-2 bg-terminal-bg rounded border border-terminal-border">
                      <div className="flex items-center gap-1 text-xs text-terminal-dim mb-1">
                        <span>📝</span>
                        <span>{lang === 'ko' ? '메모' : 'Memo'}</span>
                      </div>
                      <p className="text-xs text-terminal-muted whitespace-pre-wrap">{tc.memo}</p>
                    </div>
                  )}
                </div>
                <div className={`flex items-center gap-1 px-2 py-1 rounded ${status.color} bg-terminal-dim/30 text-xs whitespace-nowrap`}>
                  <span>{status.icon}</span>
                  <span>{status.label}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// 상태별 배지 스타일
const getStatusBadge = (status: string | null) => {
  const statusKey = (status || 'unknown') as ReleaseStatus | 'unknown';
  const configs: Record<string, { bg: string; text: string; label: string }> = {
    go: { bg: 'bg-status-go/20', text: 'text-status-go', label: 'GO' },
    conditional: { bg: 'bg-status-warning/20', text: 'text-status-warning', label: 'COND' },
    holding: { bg: 'bg-status-holding/20', text: 'text-status-holding', label: 'HOLD' },
    no_go: { bg: 'bg-status-danger/20', text: 'text-status-danger', label: 'NO-GO' },
    unknown: { bg: 'bg-terminal-dim/20', text: 'text-terminal-muted', label: '-' },
  };
  return configs[statusKey] || configs.unknown;
};

// 상태에 따른 점수 색상
const getScoreColorByStatus = (status: string | null) => {
  if (!status) return 'text-terminal-muted';
  switch (status) {
    case 'go': return 'text-status-go';           // 녹색
    case 'conditional': return 'text-status-warning';  // 노란색
    case 'holding': return 'text-status-holding';      // 주황색
    case 'no_go': return 'text-status-danger';         // 빨간색
    default: return 'text-terminal-muted';
  }
};

interface HistoryPageProps {
  onReEvaluate?: (input: ReleaseInput) => void;
  onOpenPricing?: () => void;
}

// 간단한 네비게이션 헬퍼
const navigateTo = (path: string) => {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

export default function HistoryPage({ onReEvaluate, onOpenPricing }: HistoryPageProps) {
  const { lang, t } = useLanguage();
  const { user, isLoading: authLoading } = useAuth();
  const isKo = lang === 'ko';

  // 스플릿 뷰 상태
  const [splitPosition, setSplitPosition] = useState(320); // 왼쪽 패널 너비 (px)
  const [isDragging, setIsDragging] = useState(false);
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false); // 왼쪽 패널 접기
  const containerRef = useRef<HTMLDivElement>(null);

  // 탭 상태
  const [tabs, setTabs] = useState<HistoryTabInfo[]>([]);
  const [activeTabId, setActiveTabId] = useState<number | null>(null);
  const [isTabsLoading, setIsTabsLoading] = useState(true);

  // 폴더 상태
  const [folders, setFolders] = useState<FolderInfo[]>([]);
  const [uncategorizedCount, setUncategorizedCount] = useState(0);
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set());
  // selectedFolderId: undefined = 전체, null = 분류 안됨, number = 특정 폴더
  const [selectedFolderId, setSelectedFolderId] = useState<number | null | undefined>(undefined);

  // 필터 상태
  const [statusFilter, setStatusFilter] = useState<'go' | 'conditional' | 'holding' | 'no_go' | null>(null);
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [authorFilter, setAuthorFilter] = useState<number | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // 작성자 목록 (팀 히스토리용)
  const [authors, setAuthors] = useState<{ id: number; name: string; email: string }[]>([]);

  // 히스토리 상태
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState<number | null>(null);

  // 드래그 앤 드롭 상태
  const [draggedItem, setDraggedItem] = useState<{ type: 'folder' | 'item'; id: number } | null>(null);
  const [dropTarget, setDropTarget] = useState<{ type: 'folder' | 'root' | 'uncategorized'; id: number | null } | null>(null);

  // 컨텍스트 메뉴 상태
  const [contextMenu, setContextMenu] = useState<{
    type: 'folder' | 'item';
    id: number;
    x: number;
    y: number;
  } | null>(null);

  // 수정 모달 상태
  const [editModal, setEditModal] = useState<{
    type: 'folder' | 'item';
    id: number;
    currentName: string;
  } | null>(null);
  const [editName, setEditName] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // 폴더 생성 모달 상태
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderParentId, setNewFolderParentId] = useState<number | null>(null);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);

  // 폴더 이동 모달 상태
  const [moveToFolderModal, setMoveToFolderModal] = useState<{
    itemId: number;
    currentFolderId: number | null;
  } | null>(null);
  const [moveModalNewFolderName, setMoveModalNewFolderName] = useState('');
  const [isCreatingFolderInMoveModal, setIsCreatingFolderInMoveModal] = useState(false);

  // 삭제 확인 모달 상태
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
    type: 'folder';
    id: number;
    name: string;
  } | null>(null);

  // 상세 뷰 상태
  const [detail, setDetail] = useState<HistoryDetailResponse | null>(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [quotaInfo, setQuotaInfo] = useState<QuotaInfo | null>(null);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [isDownloadingTestcase, setIsDownloadingTestcase] = useState(false);
  const [downloadingPlanningIndex, setDownloadingPlanningIndex] = useState<number | null>(null);
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  
  // 파일 다운로드 모달 상태
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [filesModalTab, setFilesModalTab] = useState<'planning' | 'testcase' | 'report'>('planning');
  
  // 기획서 이미지 로드 상태
  const [planningImages, setPlanningImages] = useState<{ url: string; name: string }[]>([]);
  const [isLoadingPlanningImages, setIsLoadingPlanningImages] = useState(false);

  // 파일 미리보기 모달 상태
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    fileName: string;
    fileType: 'csv' | 'text' | 'pdf' | 'image' | 'unknown';
    content: string | null;
    blobUrl: string | null;
    isLoading: boolean;
  }>({
    isOpen: false,
    fileName: '',
    fileType: 'unknown',
    content: null,
    blobUrl: null,
    isLoading: false,
  });

  // 스플릿 드래그 핸들러
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;
      
      const containerRect = containerRef.current.getBoundingClientRect();
      const newPosition = e.clientX - containerRect.left;
      
      // 최소 200px, 최대 (컨테이너 너비 - 400px)
      const minWidth = 200;
      const maxWidth = containerRect.width - 400;
      setSplitPosition(Math.max(minWidth, Math.min(maxWidth, newPosition)));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging]);

  // 탭 로드
  const loadTabs = useCallback(async () => {
    setIsTabsLoading(true);
    try {
      const response = await getHistoryTabs();
      setTabs(response.tabs);
      if (response.tabs.length > 0) {
        setActiveTabId(response.tabs[0].team_id);
      }
    } catch (err) {
      console.error('Failed to load history tabs:', err);
    } finally {
      setIsTabsLoading(false);
    }
  }, []);

  // 폴더 트리 로드
  const loadFolders = useCallback(async () => {
    try {
      const response = await getFoldersTree(activeTabId);
      setFolders(response.folders);
      setUncategorizedCount(response.uncategorized_count);
    } catch (err) {
      console.error('Failed to load folders:', err);
    }
  }, [activeTabId]);

  // 히스토리 로드 (선택된 폴더 + 필터에 따라)
  const loadHistory = useCallback(async () => {
    setIsLoading(true);
    try {
      // 필터 구성
      // selectedFolderId: undefined = 전체, null = 분류 안됨, number = 특정 폴더
      const filters: HistoryFilters = {
        folderId: selectedFolderId === undefined 
          ? undefined  // 전체 (폴더 필터 없음)
          : selectedFolderId === null 
            ? 'uncategorized'  // 분류 안됨
            : selectedFolderId,  // 특정 폴더
        status: statusFilter || undefined,
        dateFrom: dateFromFilter || undefined,
        dateTo: dateToFilter || undefined,
        authorId: authorFilter || undefined,
      };
      
      const response = await getHistory(1, 100, activeTabId ?? undefined, filters);
      setItems(response.items);
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setIsLoading(false);
    }
  }, [activeTabId, selectedFolderId, statusFilter, dateFromFilter, dateToFilter, authorFilter]);

  // 초기 로드
  useEffect(() => {
    if (user) {
      loadTabs();
    }
  }, [user, loadTabs]);

  // 탭 변경 시 폴더 및 히스토리 로드 + 필터 초기화
  useEffect(() => {
    if (!isTabsLoading && tabs.length > 0) {
      // 필터 초기화
      setStatusFilter(null);
      setDateFromFilter('');
      setDateToFilter('');
      setAuthorFilter(null);
      setSelectedFolderId(undefined);  // 전체로 초기화
      setAuthors([]);
      
      loadFolders();
    }
  }, [isTabsLoading, tabs.length, activeTabId, loadFolders]);

  // 필터 변경 시 히스토리 로드
  useEffect(() => {
    if (!isTabsLoading && tabs.length > 0) {
      loadHistory();
    }
  }, [isTabsLoading, tabs.length, loadHistory]);

  // 팀 히스토리 작성자 목록 로드 (필터 없이 전체 조회)
  useEffect(() => {
    const loadAuthors = async () => {
      if (activeTabId === null) {
        setAuthors([]);
        return;
      }
      
      try {
        // 필터 없이 전체 조회해서 작성자 목록 수집
        const response = await getHistory(1, 100, activeTabId, {});
        const authorMap = new Map<number, { id: number; name: string; email: string }>();
        response.items.forEach(item => {
          if (item.user_id && item.user_email) {
            authorMap.set(item.user_id, {
              id: item.user_id,
              name: item.user_name || item.user_email,
              email: item.user_email,
            });
          }
        });
        setAuthors(Array.from(authorMap.values()));
      } catch (err) {
        console.error('Failed to load authors:', err);
      }
    };
    
    if (!isTabsLoading && activeTabId !== null) {
      loadAuthors();
    }
  }, [isTabsLoading, activeTabId]);

  // 폴더 토글
  const toggleFolder = (folderId: number) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  // 컨텍스트 메뉴 닫기
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // 폴더 재귀 검색 헬퍼
  const findFolderById = useCallback((folderList: FolderInfo[], id: number): FolderInfo | null => {
    for (const folder of folderList) {
      if (folder.id === id) return folder;
      if (folder.children?.length) {
        const found = findFolderById(folder.children, id);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // 폴더 깊이 계산 헬퍼 (최상위=0, 1단계=1, 2단계=2, 3단계=3)
  const getFolderDepth = useCallback((folderList: FolderInfo[], folderId: number, depth: number = 0): number => {
    for (const folder of folderList) {
      if (folder.id === folderId) return depth;
      if (folder.children?.length) {
        const found = getFolderDepth(folder.children, folderId, depth + 1);
        if (found >= 0) return found;
      }
    }
    return -1;
  }, []);

  // 하위 폴더 생성 가능 여부 (최대 3단계까지)
  const canCreateSubfolder = useCallback((parentId: number): boolean => {
    const depth = getFolderDepth(folders, parentId);
    return depth >= 0 && depth < 2;  // 0, 1, 2단계까지만 하위 생성 가능 (결과적으로 3단계까지)
  }, [folders, getFolderDepth]);

  // 폴더 생성
  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || isCreatingFolder) return;
    
    setIsCreatingFolder(true);
    try {
      await createFolder({
        name: newFolderName.trim(),
        parent_id: newFolderParentId,
        team_id: activeTabId,
      });
      setNewFolderName('');
      setNewFolderParentId(null);
      setShowCreateFolder(false);
      loadFolders();
    } catch (err) {
      console.error('Failed to create folder:', err);
    } finally {
      setIsCreatingFolder(false);
    }
  };

  // 폴더/항목 이름 수정
  const handleSaveEdit = async () => {
    if (!editModal || !editName.trim() || isSavingEdit) return;
    
    setIsSavingEdit(true);
    try {
      if (editModal.type === 'folder') {
        await updateFolder(editModal.id, { name: editName.trim() });
        loadFolders();
      } else {
        await updateHistory(editModal.id, { feature_name: editName.trim() });
        loadHistory();
      }
      setEditModal(null);
    } catch (err) {
      console.error('Failed to update:', err);
    } finally {
      setIsSavingEdit(false);
    }
  };

  // 폴더 삭제 확인 모달 열기
  const openDeleteFolderModal = (folderId: number) => {
    const folder = findFolderById(folders, folderId);
    if (folder) {
      setDeleteConfirmModal({
        type: 'folder',
        id: folder.id,
        name: folder.name,
      });
    }
  };

  // 폴더 삭제 실행
  const handleConfirmDelete = async () => {
    if (!deleteConfirmModal) return;
    
    try {
      await deleteFolder(deleteConfirmModal.id);
      setDeleteConfirmModal(null);
      if (selectedFolderId === deleteConfirmModal.id) {
        setSelectedFolderId(null);
      }
      loadFolders();
      loadHistory();
    } catch (err) {
      console.error('Failed to delete folder:', err);
    }
  };

  // 항목 폴더 이동
  const handleMoveToFolder = async (folderId: number | null) => {
    if (!moveToFolderModal) return;
    
    try {
      await updateHistory(moveToFolderModal.itemId, { 
        folder_id: folderId === null ? 0 : folderId 
      });
      setMoveToFolderModal(null);
      loadHistory();
      loadFolders();
    } catch (err) {
      console.error('Failed to move item:', err);
    }
  };

  // 드래그 앤 드롭 핸들러 (폴더/항목 이동용)
  const handleItemDragStart = (type: 'folder' | 'item', id: number) => (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ type, id }));
    setDraggedItem({ type, id });
  };

  const handleItemDragEnd = () => {
    setDraggedItem(null);
    setDropTarget(null);
  };

  const handleItemDragOver = (targetType: 'folder' | 'root' | 'uncategorized', targetId: number | null) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // 자기 자신 위에 드롭 방지
    if (draggedItem?.type === 'folder' && targetType === 'folder' && draggedItem.id === targetId) {
      return;
    }
    
    setDropTarget({ type: targetType, id: targetId });
  };

  const handleItemDragLeave = () => {
    setDropTarget(null);
  };

  const handleItemDrop = async (targetType: 'folder' | 'root' | 'uncategorized', targetId: number | null) => {
    if (!draggedItem) return;
    
    try {
      if (draggedItem.type === 'item') {
        // 평가 항목을 폴더로 이동
        const folderId = targetType === 'uncategorized' ? null : targetId;
        await updateHistory(draggedItem.id, { 
          folder_id: folderId === null ? 0 : folderId 
        });
      } else if (draggedItem.type === 'folder') {
        // 폴더를 다른 폴더로 이동 (하위 폴더로 만들기) 또는 root로 이동
        if (targetType === 'root' || targetType === 'uncategorized') {
          // root로 이동
          await updateFolder(draggedItem.id, { parent_id: 0 });
        } else if (targetType === 'folder' && targetId !== null) {
          // 대상 폴더가 3단계인지 확인
          const targetDepth = getFolderDepth(folders, targetId);
          if (targetDepth >= 2) {
            // 3단계 이상이면 이동 불가
            console.warn('Cannot move folder: target is too deep');
            return;
          }
          await updateFolder(draggedItem.id, { parent_id: targetId });
        }
      }
      
      loadFolders();
      loadHistory();
    } catch (err) {
      console.error('Failed to move:', err);
    } finally {
      setDraggedItem(null);
      setDropTarget(null);
    }
  };

  // 이미지 파일 확인 함수
  const isImageFile = (filename: string) => {
    const ext = filename.toLowerCase().split('.').pop();
    return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext || '');
  };

  // 기획서 이미지 로드
  const loadPlanningImages = useCallback(async (fileNames: string[], recordIdParam: number) => {
    const imageFiles = fileNames
      .map((name, index) => ({ name, index }))
      .filter(({ name }) => isImageFile(name));
    
    if (imageFiles.length === 0) {
      setPlanningImages([]);
      return;
    }
    
    setIsLoadingPlanningImages(true);
    try {
      const images: { url: string; name: string }[] = [];
      
      for (const { name, index } of imageFiles) {
        try {
          const { blob } = await downloadPlanningFile(recordIdParam, index);
          const url = URL.createObjectURL(blob);
          images.push({ url, name });
        } catch (e) {
          console.error(`Failed to load image ${name}:`, e);
        }
      }
      
      setPlanningImages(images);
    } catch (e) {
      console.error('Failed to load planning images:', e);
    } finally {
      setIsLoadingPlanningImages(false);
    }
  }, []);

  // 상세 데이터 로드
  const loadDetail = useCallback(async (recordId: number) => {
    setIsDetailLoading(true);
    // 이전 이미지 URL 해제
    planningImages.forEach(img => URL.revokeObjectURL(img.url));
    setPlanningImages([]);
    
    try {
      const response = await getHistoryDetail(recordId);
      setDetail(response);
      
      // 기획서 이미지 로드
      if (response.has_planning_files && response.planning_file_names.length > 0) {
        loadPlanningImages(response.planning_file_names, recordId);
      }
    } catch (err) {
      console.error('Failed to load detail:', err);
    } finally {
      setIsDetailLoading(false);
    }
  }, [loadPlanningImages]); // eslint-disable-line react-hooks/exhaustive-deps

  // 선택 항목 변경 시 상세 로드
  useEffect(() => {
    if (selectedRecordId) {
      loadDetail(selectedRecordId);
    } else {
      setDetail(null);
    }
  }, [selectedRecordId, loadDetail]);

  // Quota 정보 로드
  useEffect(() => {
    if (user) {
      getQuotaInfo().then(setQuotaInfo).catch(console.error);
    }
  }, [user]);

  // PDF 뷰어 열기 핸들러 (모든 플랜에서 사용 가능)
  const handleOpenPdfViewer = async () => {
    if (!selectedRecordId || isPdfLoading) return;
    
    // 모달 먼저 열고 로딩 표시
    setShowPdfViewer(true);
    setIsPdfLoading(true);
    
    try {
      const blob = await downloadHistoryPdf(selectedRecordId, lang);
      // 기존 URL 해제
      if (pdfBlobUrl) {
        window.URL.revokeObjectURL(pdfBlobUrl);
      }
      const url = window.URL.createObjectURL(blob);
      setPdfBlobUrl(url);
    } catch (err) {
      console.error('PDF 로딩 오류:', err);
      // 오류 시 모달 닫기
      setShowPdfViewer(false);
    } finally {
      setIsPdfLoading(false);
    }
  };

  // PDF 다운로드 핸들러 (Pro 이상)
  const handleDownloadPdf = () => {
    if (!pdfBlobUrl || !canDownloadPdf) return;
    const sanitizedName = (detail?.feature_name || 'report').replace(/[^a-zA-Z0-9가-힣_\- ]/g, '').replace(/\s+/g, '_');
    const status = detail?.status || 'go';
    const statusSuffixMap: Record<string, string> = { go: 'GO', conditional: 'Conditional', holding: 'Holding', no_go: 'NoGo' };
    const fileName = `${sanitizedName}-${statusSuffixMap[status] || status}.pdf`;
    const link = document.createElement('a');
    link.href = pdfBlobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // PDF 뷰어 닫기
  const handleClosePdfViewer = () => {
    setShowPdfViewer(false);
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  // 테스트케이스 다운로드
  const handleDownloadTestcase = async () => {
    if (!selectedRecordId || isDownloadingTestcase) return;
    setIsDownloadingTestcase(true);
    try {
      const { blob, filename } = await downloadTestcaseFile(selectedRecordId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download testcase file:', err);
    } finally {
      setIsDownloadingTestcase(false);
    }
  };

  // 기획 파일 다운로드
  const handleDownloadPlanningFile = async (fileIndex: number) => {
    if (!selectedRecordId || downloadingPlanningIndex !== null) return;
    setDownloadingPlanningIndex(fileIndex);
    try {
      const { blob, filename } = await downloadPlanningFile(selectedRecordId, fileIndex);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download planning file:', err);
    } finally {
      setDownloadingPlanningIndex(null);
    }
  };

  // 전체 파일 ZIP 다운로드
  const handleDownloadAllFilesZip = async () => {
    if (!selectedRecordId || isDownloadingZip) return;
    setIsDownloadingZip(true);
    try {
      const { blob, filename } = await downloadAllFilesZip(selectedRecordId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setShowFilesModal(false);
    } catch (err) {
      console.error('Failed to download ZIP file:', err);
    } finally {
      setIsDownloadingZip(false);
    }
  };

  // 파일 확장자에 따른 미리보기 타입 결정
  const getFilePreviewType = (fileName: string): 'csv' | 'text' | 'pdf' | 'image' | 'unknown' => {
    const ext = fileName.toLowerCase().split('.').pop() || '';
    if (ext === 'csv') return 'csv';
    if (['txt', 'md', 'markdown', 'json', 'xml', 'html', 'log'].includes(ext)) return 'text';
    if (ext === 'pdf') return 'pdf';
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return 'image';
    return 'unknown';
  };

  // 기획서 파일 미리보기
  const handlePreviewPlanningFile = async (fileIndex: number, fileName: string) => {
    if (!selectedRecordId) return;
    
    const fileType = getFilePreviewType(fileName);
    
    // 미리보기 불가능한 파일 타입
    if (fileType === 'unknown') {
      alert(isKo ? '이 파일 형식은 미리보기를 지원하지 않습니다.' : 'Preview is not supported for this file type.');
      return;
    }
    
    setPreviewModal({
      isOpen: true,
      fileName,
      fileType,
      content: null,
      blobUrl: null,
      isLoading: true,
    });
    
    try {
      const { blob } = await downloadPlanningFile(selectedRecordId, fileIndex);
      
      if (fileType === 'csv' || fileType === 'text') {
        const text = await blob.text();
        setPreviewModal(prev => ({
          ...prev,
          content: text,
          isLoading: false,
        }));
      } else {
        // PDF, Image
        const blobUrl = window.URL.createObjectURL(blob);
        setPreviewModal(prev => ({
          ...prev,
          blobUrl,
          isLoading: false,
        }));
      }
    } catch (err) {
      console.error('Failed to preview planning file:', err);
      setPreviewModal(prev => ({ ...prev, isOpen: false }));
    }
  };

  // 테스트케이스 파일 미리보기
  const handlePreviewTestcaseFile = async (fileName: string) => {
    if (!selectedRecordId) return;
    
    const fileType = getFilePreviewType(fileName);
    
    // 미리보기 불가능한 파일 타입
    if (fileType === 'unknown') {
      alert(isKo ? '이 파일 형식은 미리보기를 지원하지 않습니다.' : 'Preview is not supported for this file type.');
      return;
    }
    
    setPreviewModal({
      isOpen: true,
      fileName,
      fileType,
      content: null,
      blobUrl: null,
      isLoading: true,
    });
    
    try {
      const { blob } = await downloadTestcaseFile(selectedRecordId);
      
      if (fileType === 'csv' || fileType === 'text') {
        const text = await blob.text();
        setPreviewModal(prev => ({
          ...prev,
          content: text,
          isLoading: false,
        }));
      } else {
        // PDF, Image
        const blobUrl = window.URL.createObjectURL(blob);
        setPreviewModal(prev => ({
          ...prev,
          blobUrl,
          isLoading: false,
        }));
      }
    } catch (err) {
      console.error('Failed to preview testcase file:', err);
      setPreviewModal(prev => ({ ...prev, isOpen: false }));
    }
  };

  // 미리보기 모달 닫기
  const closePreviewModal = () => {
    if (previewModal.blobUrl) {
      window.URL.revokeObjectURL(previewModal.blobUrl);
    }
    setPreviewModal({
      isOpen: false,
      fileName: '',
      fileType: 'unknown',
      content: null,
      blobUrl: null,
      isLoading: false,
    });
  };

  // CSV 파싱 함수
  const parseCsv = (csvText: string): string[][] => {
    const rows: string[][] = [];
    let currentRow: string[] = [];
    let currentCell = '';
    let inQuotes = false;
    
    for (let i = 0; i < csvText.length; i++) {
      const char = csvText[i];
      const nextChar = csvText[i + 1];
      
      if (inQuotes) {
        if (char === '"' && nextChar === '"') {
          currentCell += '"';
          i++;
        } else if (char === '"') {
          inQuotes = false;
        } else {
          currentCell += char;
        }
      } else {
        if (char === '"') {
          inQuotes = true;
        } else if (char === ',') {
          currentRow.push(currentCell);
          currentCell = '';
        } else if (char === '\n' || (char === '\r' && nextChar === '\n')) {
          currentRow.push(currentCell);
          rows.push(currentRow);
          currentRow = [];
          currentCell = '';
          if (char === '\r') i++;
        } else if (char !== '\r') {
          currentCell += char;
        }
      }
    }
    
    // 마지막 셀과 행 추가
    if (currentCell || currentRow.length > 0) {
      currentRow.push(currentCell);
      rows.push(currentRow);
    }
    
    return rows;
  };

  // 다시 평가하기 - 버전 번호 자동 증가
  const handleReEvaluate = () => {
    console.log('🔄 handleReEvaluate called, detail:', !!detail, 'onReEvaluate:', !!onReEvaluate);
    if (detail) {
      // 기존 제목에서 버전 번호 추출 및 증가
      let newFeatureName = detail.feature_name;
      const versionMatch = newFeatureName.match(/\s*ver\s*(\d+)\s*$/i);
      
      if (versionMatch) {
        // 기존 버전 번호 증가
        const currentVersion = parseInt(versionMatch[1], 10);
        newFeatureName = newFeatureName.replace(/\s*ver\s*\d+\s*$/i, ` ver ${currentVersion + 1}`);
      } else {
        // 버전 번호가 없으면 ver 2 추가
        newFeatureName = `${newFeatureName} ver 2`;
      }
      
      const inputData: ReleaseInput = {
        feature_name: newFeatureName,
        release_date: detail.release_date,
        planning_intent: detail.planning_intent || [],
        risks: detail.risks || [],
        qa_status: detail.qa_status || {},
        suggested_testcases: detail.suggested_testcases || [],
        github_context: (detail.github_context as unknown as Record<string, unknown> | null) ?? null,
        lang: lang,
        is_re_evaluate: true,  // 다시 평가 플래그
      };
      console.log('🔄 Calling onReEvaluate with:', inputData.feature_name);
      onReEvaluate?.(inputData);
    }
  };

  // 상태 설정
  const statusConfig: Record<ReleaseStatus, { label: string; badge: string; color: string }> = {
    go: { label: t.status.go, badge: 'badge-go', color: 'text-status-go' },
    conditional: { label: t.status.conditional, badge: 'badge-conditional', color: 'text-status-warning' },
    holding: { label: t.status.holding, badge: 'badge-holding', color: 'text-status-holding' },
    no_go: { label: t.status.noGo, badge: 'badge-nogo', color: 'text-status-danger' },
  };

  const getBarColor = (score: number, max: number) => {
    const pct = (score / max) * 100;
    if (pct >= 70) return 'bg-status-go';
    if (pct >= 40) return 'bg-status-warning';
    return 'bg-status-danger';
  };

  const getScoreGrade = (score: number) => {
    if (score >= 80) return { grade: 'A', desc: t.grade.excellent };
    if (score >= 70) return { grade: 'B', desc: t.grade.good };
    if (score >= 60) return { grade: 'C', desc: t.grade.average };
    if (score >= 50) return { grade: 'D', desc: t.grade.poor };
    return { grade: 'F', desc: t.grade.insufficient };
  };

  const getPriorityConfig = (priority: RecommendationPriority) => {
    const configs = {
      high: { color: 'text-status-danger', bg: 'bg-status-danger/10 border-status-danger/30', label: t.result.priorityHigh, icon: '🔴' },
      medium: { color: 'text-status-warning', bg: 'bg-status-warning/10 border-status-warning/30', label: t.result.priorityMedium, icon: '🟡' },
      low: { color: 'text-status-go', bg: 'bg-status-go/10 border-status-go/30', label: t.result.priorityLow, icon: '🟢' },
    };
    return configs[priority];
  };

  const canDownloadPdf = quotaInfo?.limits.pdf_download ?? false;
  
  // MAX/Team 플랜 여부 확인 (첨부 기획 이미지 사용 가능)
  const isCurrentlyMaxOrTeam = quotaInfo?.plan === 'max' || quotaInfo?.actual_plan === 'max' || quotaInfo?.plan === 'team' || quotaInfo?.team !== null;
  
  const result = detail?.result;
  const config = result ? statusConfig[result.status] : null;
  const scoreGrade = result ? getScoreGrade(result.confidence_score) : null;

  // 폴더 트리 렌더링
  const renderFolderTree = (folderList: FolderInfo[], depth: number = 0) => {
    return folderList.map(folder => {
      const isExpanded = expandedFolders.has(folder.id);
      const hasChildren = folder.children.length > 0;
      const isSelected = selectedFolderId === folder.id;
      const isDropTarget = dropTarget?.type === 'folder' && dropTarget.id === folder.id;
      const isDragging = draggedItem?.type === 'folder' && draggedItem.id === folder.id;

      return (
        <div key={folder.id}>
          <div
            className={`flex items-center gap-1 px-2 py-1.5 rounded cursor-pointer transition-colors ${
              isDropTarget 
                ? 'bg-status-go/40 ring-2 ring-status-go' 
                : isSelected 
                  ? 'bg-status-go/20 text-status-go' 
                  : 'hover:bg-terminal-dim/30 text-terminal-text'
            } ${isDragging ? 'opacity-50' : ''}`}
            style={{ paddingLeft: `${depth * 16 + 8}px` }}
            draggable
            onDragStart={handleItemDragStart('folder', folder.id)}
            onDragEnd={handleItemDragEnd}
            onDragOver={handleItemDragOver('folder', folder.id)}
            onDragLeave={handleItemDragLeave}
            onDrop={(e) => {
              e.preventDefault();
              handleItemDrop('folder', folder.id);
            }}
            onClick={() => {
              if (hasChildren) toggleFolder(folder.id);
              setSelectedFolderId(folder.id);
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              setContextMenu({
                type: 'folder',
                id: folder.id,
                x: e.clientX,
                y: e.clientY,
              });
            }}
          >
            {/* 펼침/접힘 아이콘 */}
            <button
              className="w-4 h-4 flex items-center justify-center"
              onClick={(e) => {
                e.stopPropagation();
                toggleFolder(folder.id);
              }}
            >
              {hasChildren && (
                <svg
                  className={`w-3 h-3 text-terminal-muted transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </button>
            
            {/* 폴더 아이콘 */}
            <span className="text-sm">📁</span>
            
            {/* 폴더 이름 */}
            <span className="flex-1 text-sm truncate">{folder.name}</span>
            
            {/* 항목 수 */}
            {folder.item_count > 0 && (
              <span className="text-xs text-terminal-muted">({folder.item_count})</span>
            )}
          </div>
          
          {/* 자식 폴더 */}
          {hasChildren && isExpanded && (
            <div>{renderFolderTree(folder.children, depth + 1)}</div>
          )}
        </div>
      );
    });
  };

  // 닫기 핸들러
  const handleClose = () => {
    navigateTo('/');
  };

  // 인증 로딩 중 (토큰 검증 대기)
  if (authLoading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center">
        <div className="text-terminal-muted">
          <div className="animate-spin w-8 h-8 border-2 border-terminal-muted border-t-status-go rounded-full mx-auto mb-2" />
        </div>
      </div>
    );
  }

  // 로그인 필요 - App.tsx에서 처리하므로 여기서는 fallback으로만 사용
  if (!user) {
    return (
      <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center animate-fadeIn">
        <div className="bg-terminal-bg rounded-xl p-8 text-center border border-terminal-border shadow-2xl">
          <p className="text-terminal-muted mb-4">
            {isKo ? '로그인이 필요합니다.' : 'Please log in to continue.'}
          </p>
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-status-go text-white rounded-lg hover:bg-status-go/90"
          >
            {isKo ? '홈으로' : 'Go Home'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="absolute inset-4 md:inset-6 lg:inset-8 bg-terminal-bg rounded-2xl border border-terminal-border shadow-2xl flex flex-col overflow-hidden animate-slideUp">
        {/* 헤더 */}
        <div className="flex-shrink-0 border-b border-terminal-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold text-terminal-text flex items-center gap-2">
                📜 {t.history.title}
              </h1>
            </div>
            
            {/* 탭 */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                {tabs.map(tab => (
                  <button
                    key={tab.team_id ?? 'personal'}
                    onClick={() => setActiveTabId(tab.team_id)}
                    className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                      activeTabId === tab.team_id
                        ? 'bg-status-go/20 text-status-go'
                        : 'text-terminal-muted hover:text-terminal-text hover:bg-terminal-dim/30'
                    }`}
                  >
                    {tab.team_id === null ? (isKo ? '👤 개인' : '👤 Personal') : `🏢 ${tab.team_name}`}
                    <span className="ml-1 text-xs opacity-70">({tab.count})</span>
                  </button>
                ))}
              </div>
              
              {/* 닫기 버튼 */}
              <button
                onClick={handleClose}
                className="p-2 hover:bg-terminal-dim/50 rounded-lg transition-colors text-terminal-muted hover:text-terminal-text"
                title={isKo ? '닫기' : 'Close'}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

      {/* 메인 컨텐츠 - 스플릿 뷰 */}
      <div ref={containerRef} className="flex-1 flex overflow-hidden">
        {/* 왼쪽 패널 - 폴더 트리 + 히스토리 목록 */}
        <div 
          className={`flex flex-col border-r border-terminal-border bg-terminal-bg transition-all duration-300 ease-in-out ${
            isLeftPanelCollapsed ? 'overflow-hidden' : ''
          }`}
          style={{ width: isLeftPanelCollapsed ? 0 : splitPosition }}
        >
          {/* 폴더 섹션 */}
          <div className="flex-shrink-0 border-b border-terminal-border">
            <div className="px-3 py-2 flex items-center justify-between">
              <span className="text-xs text-terminal-muted uppercase tracking-wide">
                {isKo ? '폴더' : 'Folders'}
              </span>
              <button
                onClick={() => setShowCreateFolder(true)}
                className="p-1 hover:bg-terminal-dim/30 rounded transition-colors"
                title={isKo ? '폴더 만들기' : 'Create Folder'}
              >
                <svg className="w-4 h-4 text-terminal-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
            
            <div className="max-h-48 overflow-y-auto pb-2">
              {/* 전체 보기 */}
              <div
                className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer transition-colors rounded ${
                  selectedFolderId === undefined 
                    ? 'bg-status-go/20 text-status-go' 
                    : 'hover:bg-terminal-dim/30 text-terminal-text'
                }`}
                onClick={() => setSelectedFolderId(undefined)}
              >
                <span className="text-sm">📋</span>
                <span className="flex-1 text-sm font-medium">{isKo ? '전체' : 'All'}</span>
              </div>
              
              {/* 폴더 트리 (최상위) */}
              <div
                onDragOver={handleItemDragOver('root', null)}
                onDragLeave={handleItemDragLeave}
                onDrop={(e) => {
                  e.preventDefault();
                  handleItemDrop('root', null);
                }}
              >
                {renderFolderTree(folders)}
              </div>
              
              {/* 분류 안됨 (구분선) */}
              <div className="border-t border-terminal-border my-2"></div>
              <div
                className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer transition-colors rounded ${
                  dropTarget?.type === 'uncategorized'
                    ? 'bg-status-go/40 ring-2 ring-status-go'
                    : selectedFolderId === null 
                      ? 'bg-status-go/20 text-status-go' 
                      : 'hover:bg-terminal-dim/30 text-terminal-text'
                }`}
                onClick={() => setSelectedFolderId(null)}
                onDragOver={handleItemDragOver('uncategorized', null)}
                onDragLeave={handleItemDragLeave}
                onDrop={(e) => {
                  e.preventDefault();
                  handleItemDrop('uncategorized', null);
                }}
              >
                <span className="text-sm">📄</span>
                <span className="flex-1 text-sm">{isKo ? '분류 안됨' : 'Uncategorized'}</span>
                {uncategorizedCount > 0 && (
                  <span className="text-xs text-terminal-muted">({uncategorizedCount})</span>
                )}
              </div>
            </div>
          </div>

          {/* 히스토리 목록 */}
          <div className="flex-1 overflow-y-auto">
            {/* 필터 헤더 */}
            <div className="px-3 py-2 flex items-center justify-between border-b border-terminal-border">
              <span className="text-xs text-terminal-muted uppercase tracking-wide">
                {isKo ? '평가 기록' : 'Evaluations'}
              </span>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-1.5 rounded-lg transition-colors flex items-center gap-1 ${
                  showFilters || statusFilter || dateFromFilter || dateToFilter || authorFilter
                    ? 'bg-status-go/20 text-status-go'
                    : 'hover:bg-terminal-dim/30 text-terminal-muted'
                }`}
                title={isKo ? '필터' : 'Filters'}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <svg 
                  className={`w-3 h-3 transition-transform ${showFilters ? 'rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>

            {/* 활성 필터 표시 (접힌 상태에서) */}
            {!showFilters && (statusFilter || dateFromFilter || dateToFilter || authorFilter) && (
              <div className="px-3 py-1.5 border-b border-terminal-border bg-terminal-dim/10 flex flex-wrap gap-1.5">
                {statusFilter && (
                  <span className={`px-1.5 py-0.5 text-xs rounded ${
                    statusFilter === 'go' ? 'bg-status-go/20 text-status-go' :
                    statusFilter === 'conditional' ? 'bg-status-warning/20 text-status-warning' :
                    statusFilter === 'holding' ? 'bg-status-holding/20 text-status-holding' :
                    'bg-status-danger/20 text-status-danger'
                  }`}>
                    {statusFilter === 'go' ? 'Go' : 
                     statusFilter === 'conditional' ? 'Cond.' : 
                     statusFilter === 'holding' ? 'Hold.' : 'No-Go'}
                  </span>
                )}
                {dateFromFilter && (
                  <span className="px-1.5 py-0.5 text-xs rounded bg-terminal-dim/30 text-terminal-text">
                    {dateFromFilter} ~
                  </span>
                )}
                {dateToFilter && (
                  <span className="px-1.5 py-0.5 text-xs rounded bg-terminal-dim/30 text-terminal-text">
                    ~ {dateToFilter}
                  </span>
                )}
                {authorFilter && (
                  <span className="px-1.5 py-0.5 text-xs rounded bg-terminal-dim/30 text-terminal-text">
                    👤 {authors.find(a => a.id === authorFilter)?.name || ''}
                  </span>
                )}
              </div>
            )}

            {/* 필터 패널 */}
            {showFilters && (
              <div className="px-3 py-3 border-b border-terminal-border bg-terminal-dim/10 space-y-2.5 min-w-[200px]">
                {/* 결과 필터 - 라벨과 버튼을 같은 줄에 */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-terminal-muted whitespace-nowrap">
                    {isKo ? '결과' : 'Result'}
                  </span>
                  <div className="flex gap-1">
                    {[
                      { value: null, label: isKo ? '전체' : 'All', color: 'text-terminal-text' },
                      { value: 'go' as const, label: 'Go', color: 'text-status-go' },
                      { value: 'conditional' as const, label: 'Cond.', color: 'text-status-warning' },
                      { value: 'holding' as const, label: 'Hold.', color: 'text-status-holding' },
                      { value: 'no_go' as const, label: 'No-Go', color: 'text-status-danger' },
                    ].map(option => (
                      <button
                        key={option.value ?? 'all'}
                        onClick={() => setStatusFilter(option.value)}
                        className={`px-1.5 py-0.5 text-xs rounded transition-colors whitespace-nowrap ${
                          statusFilter === option.value
                            ? `bg-terminal-dim/50 ${option.color} font-medium`
                            : 'text-terminal-muted hover:bg-terminal-dim/30'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 날짜 필터 - 세로 배치 (생성일 기준) */}
                <div className="space-y-1.5">
                  <span className="text-xs text-terminal-muted/70">
                    {isKo ? '생성일 기준' : 'By creation date'}
                  </span>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-terminal-muted whitespace-nowrap w-10">
                      {isKo ? '시작' : 'From'}
                    </label>
                    <input
                      type="date"
                      value={dateFromFilter}
                      onChange={(e) => setDateFromFilter(e.target.value)}
                      className="w-[130px] px-1.5 py-1 text-xs bg-terminal-input border border-terminal-border rounded text-terminal-text"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-terminal-muted whitespace-nowrap w-10">
                      {isKo ? '종료' : 'To'}
                    </label>
                    <input
                      type="date"
                      value={dateToFilter}
                      onChange={(e) => setDateToFilter(e.target.value)}
                      className="w-[130px] px-1.5 py-1 text-xs bg-terminal-input border border-terminal-border rounded text-terminal-text"
                    />
                  </div>
                </div>

                {/* 작성자 필터 (팀 히스토리용) */}
                {activeTabId !== null && authors.length > 0 && (
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-terminal-muted whitespace-nowrap w-10">
                      {isKo ? '작성자' : 'Author'}
                    </label>
                    <select
                      value={authorFilter ?? ''}
                      onChange={(e) => setAuthorFilter(e.target.value ? Number(e.target.value) : null)}
                      className="flex-1 px-2 py-1 text-xs bg-terminal-input border border-terminal-border rounded text-terminal-text min-w-0"
                    >
                      <option value="">{isKo ? '전체' : 'All'}</option>
                      {authors.map(author => (
                        <option key={author.id} value={author.id}>
                          {author.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* 필터 초기화 */}
                {(statusFilter || dateFromFilter || dateToFilter || authorFilter) && (
                  <button
                    onClick={() => {
                      setStatusFilter(null);
                      setDateFromFilter('');
                      setDateToFilter('');
                      setAuthorFilter(null);
                    }}
                    className="text-xs text-status-danger hover:underline mt-1"
                  >
                    {isKo ? '필터 초기화' : 'Clear Filters'}
                  </button>
                )}
              </div>
            )}
            
            {isLoading || isTabsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-status-go/30 border-t-status-go rounded-full animate-spin"></div>
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-terminal-muted">
                {(statusFilter || dateFromFilter || dateToFilter || authorFilter) ? (
                  <>
                    {/* 필터 결과 없음 일러스트 */}
                    <svg className="w-16 h-16 mb-4 opacity-40" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="28" cy="28" r="18" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                      <path d="M41 41L54 54" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                      <path d="M22 28H34" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    <p className="text-sm">{isKo ? '필터 조건에 맞는 평가 기록이 없습니다' : 'No evaluations matching the filter'}</p>
                    <p className="text-xs mt-1 opacity-60">{isKo ? '다른 조건으로 검색해 보세요' : 'Try different filter options'}</p>
                  </>
                ) : (
                  <>
                    {/* 기록 없음 일러스트 */}
                    <svg className="w-16 h-16 mb-4 opacity-40" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="12" y="8" width="40" height="48" rx="4" stroke="currentColor" strokeWidth="2.5"/>
                      <path d="M20 20H44" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M20 28H38" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M20 36H42" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <circle cx="32" cy="46" r="2" fill="currentColor" opacity="0.5"/>
                    </svg>
                    <p className="text-sm">{isKo ? '평가 기록이 없습니다' : 'No evaluations'}</p>
                    <p className="text-xs mt-1 opacity-60">{isKo ? '새 평가를 시작해 보세요' : 'Start a new evaluation'}</p>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-1 px-2 pb-4">
                {items.map(item => {
                  const statusBadge = getStatusBadge(item.status);
                  const isSelected = selectedRecordId === item.id;
                  const isDragging = draggedItem?.type === 'item' && draggedItem.id === item.id;
                  
                  return (
                    <div
                      key={item.id}
                      className={`p-3 rounded-lg cursor-grab transition-colors ${
                        isSelected 
                          ? 'bg-status-go/20 border border-status-go/50' 
                          : 'bg-terminal-dim/10 hover:bg-terminal-dim/20 border border-transparent'
                      } ${isDragging ? 'opacity-50 cursor-grabbing' : ''}`}
                      draggable
                      onDragStart={handleItemDragStart('item', item.id)}
                      onDragEnd={handleItemDragEnd}
                      onClick={() => setSelectedRecordId(item.id)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setContextMenu({
                          type: 'item',
                          id: item.id,
                          x: e.clientX,
                          y: e.clientY,
                        });
                      }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm text-terminal-text truncate">
                            {item.feature_name}
                          </h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-terminal-muted">
                              {item.release_date}
                            </span>
                            <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${statusBadge.bg} ${statusBadge.text}`}>
                              {statusBadge.label}
                            </span>
                          </div>
                        </div>
                        <div className={`text-lg font-bold ${getScoreColorByStatus(item.status)}`}>
                          {item.confidence_score ?? '-'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* 드래그 핸들 + 접기/펼치기 버튼 */}
        <div className="relative flex-shrink-0 group">
          {/* 드래그 핸들 */}
          {!isLeftPanelCollapsed && (
            <div
              className={`w-1 h-full cursor-col-resize transition-colors ${
                isDragging ? 'bg-status-go' : 'bg-terminal-border hover:bg-status-go/50'
              }`}
              onMouseDown={handleDragStart}
            />
          )}
          {/* 접기/펼치기 버튼 */}
          <button
            onClick={() => setIsLeftPanelCollapsed(!isLeftPanelCollapsed)}
            className={`absolute top-1/2 -translate-y-1/2 z-10 w-5 h-10 flex items-center justify-center bg-terminal-dim border border-terminal-border rounded-r-md hover:bg-terminal-dim/80 hover:border-status-go/50 transition-all ${
              isLeftPanelCollapsed ? 'left-0' : '-right-2 opacity-0 group-hover:opacity-100'
            }`}
            title={isLeftPanelCollapsed ? (isKo ? '패널 펼치기' : 'Expand panel') : (isKo ? '패널 접기' : 'Collapse panel')}
          >
            <svg 
              className={`w-3 h-3 text-terminal-muted transition-transform duration-200 ${isLeftPanelCollapsed ? '' : 'rotate-180'}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* 오른쪽 패널 - 상세 보기 (인라인) */}
        <div className="flex-1 overflow-y-auto bg-terminal-bg">
          {selectedRecordId && isDetailLoading ? (
            <div className="h-full flex items-center justify-center">
              <div className="w-8 h-8 border-3 border-status-go/30 border-t-status-go rounded-full animate-spin"></div>
            </div>
          ) : selectedRecordId && detail && result && config && scoreGrade ? (
            <div className="p-6 space-y-4">
              {/* 헤더 */}
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-terminal-text">{detail.feature_name}</h2>
                    {detail.created_with_plan && (
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                        detail.created_with_plan === 'team' 
                          ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                          : detail.created_with_plan === 'max'
                          ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                          : detail.created_with_plan === 'pro'
                          ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                          : 'bg-terminal-dim/50 text-terminal-muted border border-terminal-border'
                      }`}>
                        {detail.created_with_plan.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-terminal-muted mt-1">
                    {detail.release_date} • {parseUTCDate(detail.created_at).toLocaleDateString(lang === 'ko' ? 'ko-KR' : 'en-US')}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedRecordId(null)}
                  className="p-2 hover:bg-terminal-dim/30 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-terminal-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* 점수 및 상태 */}
              <div className="card p-4">
                <div className="flex items-end gap-4">
                  <div>
                    <div className="text-terminal-muted text-xs mb-1">{t.result.confidenceScore}</div>
                    <div className={`text-5xl font-bold ${config.color}`}>
                      {result.confidence_score}
                      <span className="text-terminal-dim text-2xl">/100</span>
                    </div>
                  </div>
                  <div className="pb-2">
                    <span className={`text-2xl font-bold ${config.color}`}>{scoreGrade.grade}</span>
                    <span className="text-terminal-muted text-sm ml-2">({scoreGrade.desc})</span>
                  </div>
                  <div className="flex-1"></div>
                  <span className={`px-3 py-1 rounded text-sm font-bold ${config.badge}`}>{config.label}</span>
                </div>
              </div>

              {/* 입력 데이터 (기획/리스크/QA/GitHub) */}
              {(detail.planning_intent?.length > 0 ||
                detail.risks?.length > 0 ||
                Object.keys(detail.qa_status || {}).length > 0 ||
                (detail.github_context as unknown as GithubContextForReport | null)?.commits?.length) && (
                <div className="card p-4">
                  <div className="text-terminal-muted text-xs mb-3 uppercase tracking-wide"># {t.result.inputData}</div>
                  <div className="space-y-4">
                    <GitHubReportBlock
                      lang={lang}
                      githubCtx={(detail.github_context as unknown as GithubContextForReport | null) ?? null}
                      inputData={{
                        planning_intent: detail.planning_intent || [],
                        risks: detail.risks || [],
                        suggested_testcases: detail.suggested_testcases || [],
                      }}
                    />

                    {/* 첨부된 기획서 이미지 (MAX/Team 플랜 전용) */}
                    {isCurrentlyMaxOrTeam && (planningImages.length > 0 || isLoadingPlanningImages) && (
                      <div className="p-3 bg-terminal-dim/10 rounded-lg border border-terminal-border">
                        <div className="text-terminal-dim text-xs mb-2 flex items-center gap-2">
                          <span>🖼️</span>
                          <span>{isKo ? '첨부된 기획 이미지' : 'Attached Planning Images'}</span>
                          <span className="px-1.5 py-0.5 bg-purple-500/30 text-purple-400 text-xs font-semibold rounded">MAX</span>
                        </div>
                        {isLoadingPlanningImages ? (
                          <div className="flex items-center justify-center py-4">
                            <div className="w-6 h-6 border-2 border-status-go/30 border-t-status-go rounded-full animate-spin"></div>
                            <span className="ml-2 text-sm text-terminal-muted">
                              {isKo ? '이미지 로딩 중...' : 'Loading images...'}
                            </span>
                          </div>
                        ) : (
                          <div className="grid gap-3">
                            {planningImages.map((img, index) => (
                              <div key={index} className="relative group">
                                <div className="bg-terminal-dim/20 rounded-lg border border-terminal-border overflow-hidden">
                                  <div className="p-2 bg-terminal-dim/30 border-b border-terminal-border">
                                    <span className="text-xs text-terminal-muted truncate block">{img.name}</span>
                                  </div>
                                  <div className="p-2 flex justify-center">
                                    <img
                                      src={img.url}
                                      alt={img.name}
                                      className="max-w-full max-h-64 object-contain rounded cursor-pointer hover:opacity-90 transition-opacity"
                                      onClick={() => window.open(img.url, '_blank')}
                                      title={isKo ? '클릭하여 크게 보기' : 'Click to view larger'}
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {detail.planning_intent?.length > 0 && (
                      <div>
                        <div className="text-terminal-dim text-xs mb-1">{t.form.planningIntent}</div>
                        <ul className="text-sm text-terminal-text space-y-1">
                          {detail.planning_intent.map((intent, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-terminal-dim">•</span>
                              <span>{stripBullet(intent)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {detail.risks?.length > 0 && (
                      <div>
                        <div className="text-terminal-dim text-xs mb-1">{t.form.risks}</div>
                        <ul className="text-sm text-terminal-text space-y-1">
                          {detail.risks.map((risk, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-status-warning">⚠</span>
                              <span>{stripBullet(risk)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {detail.qa_status && Object.keys(detail.qa_status).length > 0 && (
                      <div>
                        <div className="text-terminal-dim text-xs mb-1">{t.form.qaStatus}</div>
                        <div className="space-y-1">
                          {Object.entries(detail.qa_status).map(([key, value]) => (
                            <div key={key} className="flex items-center gap-2 text-sm">
                              <span className="text-terminal-muted">{key}:</span>
                              <span
                                className={`${
                                  value.includes('검증 완료') || value.includes('Verified')
                                    ? 'text-status-go'
                                    : value.includes('미검증') || value.includes('Unverified')
                                      ? 'text-status-danger'
                                      : 'text-status-warning'
                                }`}
                              >
                                {value}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* TRI 분석 (삼각형 레이더 차트) - MAX/Team 플랜 전용 */}
              {isCurrentlyMaxOrTeam && (detail?.created_with_plan === 'max' || detail?.created_with_plan === 'team') && result.breakdown && (() => {
                // breakdown에서 점수 추출
                const breakdown = result.breakdown ?? [];
                const planningItem = breakdown.find(b => 
                  b.category.toLowerCase().includes('planning') || b.category_ko?.includes('기획')
                );
                const riskItem = breakdown.find(b => 
                  b.category.toLowerCase().includes('risk') || b.category_ko?.includes('리스크')
                );
                const testItem = breakdown.find(b => 
                  b.category.toLowerCase().includes('test') || b.category.toLowerCase().includes('qa') ||
                  b.category_ko?.includes('테스트') || b.category_ko?.includes('QA')
                );

                const radarScores = {
                  planning: planningItem ? Math.round((planningItem.score / planningItem.max_score) * 100) : 70,
                  risk: riskItem ? Math.round((riskItem.score / riskItem.max_score) * 100) : 65,
                  test: testItem ? Math.round((testItem.score / testItem.max_score) * 100) : 75,
                };

                const getScoreColor = (score: number) => score >= 80 ? 'text-status-go' : score >= 60 ? 'text-status-warning' : 'text-status-danger';

                return (
                  <div className="card p-4 border-purple-500/30 bg-gradient-to-br from-purple-500/5 to-transparent">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-terminal-muted text-xs uppercase tracking-wide">
                        # {isKo ? 'TRI 분석' : 'TRI Analysis'}
                        <span className="text-terminal-dim normal-case ml-2">
                          ({isKo ? '기획-리스크-테스트 균형' : 'Planning-Risk-Test Balance'})
                        </span>
                      </div>
                      <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs font-semibold rounded border border-purple-500/30">
                        {detail.created_with_plan.toUpperCase()}
                      </span>
                    </div>
                    
                    <div className="flex justify-center">
                      <TriRadarChart
                        planning={radarScores.planning}
                        risk={radarScores.risk}
                        testCoverage={radarScores.test}
                        size={220}
                      />
                    </div>
                    
                    {/* 축별 점수 */}
                    <div className="grid grid-cols-3 gap-2 mt-4">
                      <div className="p-2 bg-terminal-dim/10 rounded-lg border border-terminal-border text-center">
                        <span className="text-base">📋</span>
                        <div className="text-xs text-terminal-muted mt-1">{isKo ? '기획' : 'Planning'}</div>
                        <div className={`text-lg font-bold ${getScoreColor(radarScores.planning)}`}>
                          {radarScores.planning}
                        </div>
                      </div>
                      <div className="p-2 bg-terminal-dim/10 rounded-lg border border-terminal-border text-center">
                        <span className="text-base">⚠️</span>
                        <div className="text-xs text-terminal-muted mt-1">{isKo ? '리스크' : 'Risk'}</div>
                        <div className={`text-lg font-bold ${getScoreColor(radarScores.risk)}`}>
                          {radarScores.risk}
                        </div>
                      </div>
                      <div className="p-2 bg-terminal-dim/10 rounded-lg border border-terminal-border text-center">
                        <span className="text-base">✅</span>
                        <div className="text-xs text-terminal-muted mt-1">{isKo ? '테스트' : 'Test'}</div>
                        <div className={`text-lg font-bold ${getScoreColor(radarScores.test)}`}>
                          {radarScores.test}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* 상세 점수 */}
              <div className="card p-4">
                <div className="text-terminal-muted text-xs mb-3 uppercase tracking-wide"># {t.result.itemScores}</div>
                <div className="space-y-3">
                  {result.breakdown.map((item) => {
                    const pct = Math.round((item.score / item.max_score) * 100);
                    const categoryLabel = lang === 'ko' ? item.category_ko : item.category;
                    return (
                      <div key={item.category}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-terminal-muted">{categoryLabel}</span>
                          <span className="text-terminal-text">
                            {item.score}<span className="text-terminal-dim">/{item.max_score}</span>
                            <span className="text-terminal-dim text-xs ml-2">({pct}%)</span>
                          </span>
                        </div>
                        <div className="progress-bar">
                          <div className={`progress-fill ${getBarColor(item.score, item.max_score)}`} style={{ width: `${pct}%` }} />
                        </div>
                        <div className="text-terminal-dim text-xs mt-1">{item.feedback}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 블로커 */}
              {result.blockers.length > 0 && (
                <div className="card p-4 border-status-danger">
                  <div className="text-status-danger text-xs mb-3 uppercase tracking-wide"># {t.result.blockers}</div>
                  <ul className="space-y-2">
                    {result.blockers.map((blocker, i) => (
                      <li key={i} className="text-sm text-status-danger flex items-start gap-2">
                        <span className="text-terminal-dim">[{i + 1}]</span>
                        <span>{blocker}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* 권고사항 */}
              {result.structured_recommendations && result.structured_recommendations.length > 0 && (
                <div className="card p-4">
                  <div className="text-status-warning text-xs mb-4 uppercase tracking-wide"># {t.result.recommendations}</div>
                  <div className="space-y-4">
                    {result.structured_recommendations.map((rec: StructuredRecommendation, i: number) => {
                      const priorityConfig = getPriorityConfig(rec.priority);
                      return (
                        <div key={i} className={`border rounded-lg p-4 ${priorityConfig.bg}`}>
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className={`font-semibold text-sm ${priorityConfig.color}`}>
                              {priorityConfig.icon} {rec.title}
                            </h4>
                            <span className={`text-xs px-2 py-0.5 rounded ${priorityConfig.color} border border-current/30`}>
                              {priorityConfig.label}
                            </span>
                          </div>
                          <p className="text-terminal-muted text-sm mb-3">{rec.description}</p>
                          {rec.action_items.length > 0 && (
                            <ul className="space-y-1">
                              {rec.action_items.map((item, j) => (
                                <li key={j} className="text-sm text-terminal-text flex items-start gap-2">
                                  <span className="text-terminal-dim">•</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* AI 제안 테스트 케이스 */}
              {detail && detail.suggested_testcases && detail.suggested_testcases.length > 0 && (
                <SuggestedTestcasesResult testcases={detail.suggested_testcases} lang={lang} />
              )}

              {/* 액션 버튼 */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-terminal-border">
                {/* 첨부파일 다운로드 버튼 */}
                <button
                  onClick={() => {
                    setFilesModalTab('planning');  // 기획서 탭을 기본으로 선택
                    setShowFilesModal(true);
                  }}
                  className="px-4 py-2 border border-status-go text-status-go rounded-lg text-sm hover:bg-status-go/10 flex items-center gap-2"
                >
                  📥 {isKo ? '첨부파일' : 'Attachments'}
                </button>
                <button
                  onClick={handleReEvaluate}
                  className="px-4 py-2 bg-status-go text-white rounded-lg text-sm hover:bg-status-go/90 ml-auto"
                >
                  {t.history.reEvaluate}
                </button>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-terminal-muted">
              <div className="text-center">
                <div className="text-4xl mb-4">📋</div>
                <p>{isKo ? '평가 기록을 선택하세요' : 'Select an evaluation'}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </div>

      {/* 컨텍스트 메뉴 */}
      {contextMenu && (
        <div
          className="fixed z-50 bg-terminal-bg border border-terminal-border rounded-lg shadow-xl py-1 min-w-[160px]"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.type === 'folder' ? (
            <>
              <button
                className="w-full px-3 py-2 text-left text-sm text-terminal-text hover:bg-terminal-dim/30 flex items-center gap-2"
                onClick={() => {
                  const folder = findFolderById(folders, contextMenu.id);
                  if (folder) {
                    setEditModal({ type: 'folder', id: folder.id, currentName: folder.name });
                    setEditName(folder.name);
                  }
                  setContextMenu(null);
                }}
              >
                <span>✏️</span>
                {isKo ? '이름 수정' : 'Rename'}
              </button>
              {canCreateSubfolder(contextMenu.id) && (
                <button
                  className="w-full px-3 py-2 text-left text-sm text-terminal-text hover:bg-terminal-dim/30 flex items-center gap-2"
                  onClick={() => {
                    setNewFolderParentId(contextMenu.id);
                    setShowCreateFolder(true);
                    setContextMenu(null);
                  }}
                >
                  <span>📁</span>
                  {isKo ? '하위 폴더 만들기' : 'Create Subfolder'}
                </button>
              )}
              <button
                className="w-full px-3 py-2 text-left text-sm text-status-danger hover:bg-terminal-dim/30 flex items-center gap-2"
                onClick={() => {
                  openDeleteFolderModal(contextMenu.id);
                  setContextMenu(null);
                }}
              >
                <span>🗑️</span>
                {isKo ? '삭제' : 'Delete'}
              </button>
            </>
          ) : (
            <>
              <button
                className="w-full px-3 py-2 text-left text-sm text-terminal-text hover:bg-terminal-dim/30 flex items-center gap-2"
                onClick={() => {
                  const item = items.find(i => i.id === contextMenu.id);
                  if (item) {
                    setEditModal({ type: 'item', id: item.id, currentName: item.feature_name });
                    setEditName(item.feature_name);
                  }
                  setContextMenu(null);
                }}
              >
                <span>✏️</span>
                {isKo ? '제목 수정' : 'Rename'}
              </button>
              <button
                className="w-full px-3 py-2 text-left text-sm text-terminal-text hover:bg-terminal-dim/30 flex items-center gap-2"
                onClick={() => {
                  setMoveToFolderModal({
                    itemId: contextMenu.id,
                    currentFolderId: selectedFolderId ?? null,
                  });
                  setContextMenu(null);
                }}
              >
                <span>📁</span>
                {isKo ? '폴더로 이동' : 'Move to Folder'}
              </button>
            </>
          )}
        </div>
      )}

      {/* 폴더 생성 모달 */}
      {showCreateFolder && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => {
            setShowCreateFolder(false);
            setNewFolderParentId(null);
            setNewFolderName('');
          }}
        >
          <div
            className="bg-terminal-bg border border-terminal-border rounded-xl p-6 w-full max-w-sm shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-terminal-text mb-2">
              {isKo ? '새 폴더 만들기' : 'Create New Folder'}
            </h3>
            {newFolderParentId && (
              <p className="text-sm text-terminal-muted mb-4 flex items-center gap-1">
                <span>📁</span>
                {(() => {
                  const parent = findFolderById(folders, newFolderParentId);
                  return parent ? (
                    <>
                      {isKo ? '위치:' : 'In:'} <span className="text-terminal-text">{parent.name}</span>
                    </>
                  ) : null;
                })()}
              </p>
            )}
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder={isKo ? '폴더 이름' : 'Folder name'}
              className="w-full px-3 py-2 bg-terminal-input border border-terminal-border rounded-lg text-terminal-text mb-4"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isCreatingFolder) handleCreateFolder();
                if (e.key === 'Escape' && !isCreatingFolder) {
                  setShowCreateFolder(false);
                  setNewFolderParentId(null);
                  setNewFolderName('');
                }
              }}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowCreateFolder(false);
                  setNewFolderParentId(null);
                  setNewFolderName('');
                }}
                className="px-4 py-2 text-terminal-muted hover:text-terminal-text"
              >
                {isKo ? '취소' : 'Cancel'}
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim() || isCreatingFolder}
                className="px-4 py-2 bg-status-go text-white rounded-lg hover:bg-status-go/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreatingFolder 
                  ? (isKo ? '생성 중...' : 'Creating...') 
                  : (isKo ? '만들기' : 'Create')
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 이름 수정 모달 */}
      {editModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setEditModal(null)}
        >
          <div
            className="bg-terminal-bg border border-terminal-border rounded-xl p-6 w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-terminal-text mb-4">
              {editModal.type === 'folder' 
                ? (isKo ? '폴더 이름 수정' : 'Rename Folder')
                : (isKo ? '제목 수정' : 'Rename')
              }
            </h3>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full px-3 py-2 bg-terminal-input border border-terminal-border rounded-lg text-terminal-text mb-4"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isSavingEdit) handleSaveEdit();
                if (e.key === 'Escape' && !isSavingEdit) setEditModal(null);
              }}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setEditModal(null)}
                disabled={isSavingEdit}
                className="px-4 py-2 text-terminal-muted hover:text-terminal-text disabled:opacity-50"
              >
                {isKo ? '취소' : 'Cancel'}
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={!editName.trim() || isSavingEdit}
                className="px-4 py-2 bg-status-go text-white rounded-lg hover:bg-status-go/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingEdit 
                  ? (isKo ? '저장 중...' : 'Saving...') 
                  : (isKo ? '저장' : 'Save')
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 폴더 이동 모달 */}
      {moveToFolderModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => {
            setMoveToFolderModal(null);
            setMoveModalNewFolderName('');
          }}
        >
          <div
            className="bg-terminal-bg border border-terminal-border rounded-xl p-6 w-full max-w-sm max-h-[70vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-terminal-text mb-4">
              {isKo ? '폴더로 이동' : 'Move to Folder'}
            </h3>
            
            {/* 새 폴더 생성 인라인 UI */}
            <div className="mb-3 pb-3 border-b border-terminal-border">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={moveModalNewFolderName}
                  onChange={(e) => setMoveModalNewFolderName(e.target.value)}
                  placeholder={isKo ? '새 폴더 이름' : 'New folder name'}
                  className="flex-1 px-3 py-2 text-sm bg-terminal-input border border-terminal-border rounded-lg text-terminal-text placeholder:text-terminal-muted"
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter' && moveModalNewFolderName.trim() && !isCreatingFolderInMoveModal) {
                      setIsCreatingFolderInMoveModal(true);
                      try {
                        const newFolder = await createFolder({
                          name: moveModalNewFolderName.trim(),
                          parent_id: null,
                          team_id: activeTabId,
                        });
                        await loadFolders();
                        setMoveModalNewFolderName('');
                        // 생성된 폴더로 바로 이동
                        if (newFolder?.id) {
                          await handleMoveToFolder(newFolder.id);
                        }
                      } catch (err) {
                        console.error('Failed to create folder:', err);
                      } finally {
                        setIsCreatingFolderInMoveModal(false);
                      }
                    }
                  }}
                />
                <button
                  onClick={async () => {
                    if (!moveModalNewFolderName.trim() || isCreatingFolderInMoveModal) return;
                    setIsCreatingFolderInMoveModal(true);
                    try {
                      const newFolder = await createFolder({
                        name: moveModalNewFolderName.trim(),
                        parent_id: null,
                        team_id: activeTabId,
                      });
                      await loadFolders();
                      setMoveModalNewFolderName('');
                      // 생성된 폴더로 바로 이동
                      if (newFolder?.id) {
                        await handleMoveToFolder(newFolder.id);
                      }
                    } catch (err) {
                      console.error('Failed to create folder:', err);
                    } finally {
                      setIsCreatingFolderInMoveModal(false);
                    }
                  }}
                  disabled={!moveModalNewFolderName.trim() || isCreatingFolderInMoveModal}
                  className="px-3 py-2 text-sm bg-status-go/20 text-status-go border border-status-go/50 rounded-lg hover:bg-status-go/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  {isCreatingFolderInMoveModal ? (
                    <span className="animate-spin">⏳</span>
                  ) : (
                    <>
                      <span>➕</span>
                      <span>{isKo ? '생성' : 'Create'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto space-y-1">
              {/* 분류 해제 */}
              <button
                onClick={() => handleMoveToFolder(null)}
                className="w-full px-3 py-2 text-left text-sm rounded-lg hover:bg-terminal-dim/30 flex items-center gap-2"
              >
                <span>📄</span>
                <span>{isKo ? '분류 안됨' : 'Uncategorized'}</span>
              </button>
              
              {/* 폴더 목록 */}
              {folders.map(folder => (
                <button
                  key={folder.id}
                  onClick={() => handleMoveToFolder(folder.id)}
                  className={`w-full px-3 py-2 text-left text-sm rounded-lg hover:bg-terminal-dim/30 flex items-center gap-2 ${
                    moveToFolderModal.currentFolderId === folder.id ? 'bg-status-go/20 text-status-go' : ''
                  }`}
                >
                  <span>📁</span>
                  <span>{folder.name}</span>
                </button>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => {
                  setMoveToFolderModal(null);
                  setMoveModalNewFolderName('');
                }}
                className="px-4 py-2 text-terminal-muted hover:text-terminal-text"
              >
                {isKo ? '취소' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {deleteConfirmModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setDeleteConfirmModal(null)}
        >
          <div
            className="bg-terminal-bg border border-terminal-border rounded-xl p-6 w-full max-w-sm shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-status-danger/20 flex items-center justify-center">
                <span className="text-xl">⚠️</span>
              </div>
              <h3 className="text-lg font-bold text-terminal-text">
                {isKo ? '폴더 삭제' : 'Delete Folder'}
              </h3>
            </div>
            
            <p className="text-terminal-muted mb-2">
              {isKo 
                ? <>폴더 <span className="text-terminal-text font-medium">"{deleteConfirmModal.name}"</span>을(를) 삭제하시겠습니까?</>
                : <>Delete folder <span className="text-terminal-text font-medium">"{deleteConfirmModal.name}"</span>?</>
              }
            </p>
            <p className="text-sm text-status-warning mb-6">
              {isKo 
                ? '⚠️ 폴더 내 항목은 "분류 안됨"으로 이동됩니다.'
                : '⚠️ Items in this folder will be moved to "Uncategorized".'
              }
            </p>
            
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteConfirmModal(null)}
                className="px-4 py-2 bg-terminal-dim/50 text-terminal-text rounded-lg hover:bg-terminal-dim/80 font-medium border border-terminal-border"
              >
                {isKo ? '취소' : 'Cancel'}
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-status-danger/20 text-status-danger rounded-lg hover:bg-status-danger/30 border border-status-danger/50"
              >
                {isKo ? '삭제' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 첨부 파일 다운로드 모달 */}
      {showFilesModal && detail && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setShowFilesModal(false)}
        >
          <div
            className="bg-terminal-bg border border-terminal-border rounded-xl p-6 w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-terminal-text flex items-center gap-2">
                📥 {isKo ? '다운로드' : 'Download'}
              </h3>
              <button
                onClick={() => setShowFilesModal(false)}
                className="p-1 hover:bg-terminal-dim/30 rounded transition-colors text-terminal-muted"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* 탭 - 기획서 / 테스트케이스 / 리포트 순서 */}
            <div className="flex border-b border-terminal-border mb-4">
              <button
                onClick={() => isCurrentlyMaxOrTeam && setFilesModalTab('planning')}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors relative ${
                  !isCurrentlyMaxOrTeam
                    ? 'text-terminal-muted/50 cursor-not-allowed'
                    : filesModalTab === 'planning'
                      ? 'text-status-go'
                      : 'text-terminal-muted hover:text-terminal-text'
                }`}
                title={!isCurrentlyMaxOrTeam ? (isKo ? 'MAX 플랜에서 사용 가능' : 'Available for MAX plan') : undefined}
              >
                <span className="flex items-center justify-center gap-1.5">
                  {!isCurrentlyMaxOrTeam ? '🔒' : '📄'} {isKo ? '기획서' : 'Planning'}
                  {isCurrentlyMaxOrTeam && detail.has_planning_files && (
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      filesModalTab === 'planning' ? 'bg-status-go/20 text-status-go' : 'bg-terminal-dim/30 text-terminal-muted'
                    }`}>
                      {detail.planning_file_names?.length || 0}
                    </span>
                  )}
                  {!isCurrentlyMaxOrTeam && (
                    <span className="px-1 py-0.5 bg-purple-500/30 text-purple-400 text-xs font-semibold rounded">MAX</span>
                  )}
                </span>
                {filesModalTab === 'planning' && isCurrentlyMaxOrTeam && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-status-go" />
                )}
              </button>
              <button
                onClick={() => setFilesModalTab('testcase')}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors relative ${
                  filesModalTab === 'testcase'
                    ? 'text-status-go'
                    : 'text-terminal-muted hover:text-terminal-text'
                }`}
              >
                <span className="flex items-center justify-center gap-1.5">
                  📊 {isKo ? '테스트케이스' : 'Testcase'}
                  {detail.has_testcase_file && (
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      filesModalTab === 'testcase' ? 'bg-status-go/20 text-status-go' : 'bg-terminal-dim/30 text-terminal-muted'
                    }`}>
                      1
                    </span>
                  )}
                </span>
                {filesModalTab === 'testcase' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-status-go" />
                )}
              </button>
              <button
                onClick={() => setFilesModalTab('report')}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors relative ${
                  filesModalTab === 'report'
                    ? 'text-status-go'
                    : 'text-terminal-muted hover:text-terminal-text'
                }`}
              >
                <span className="flex items-center justify-center gap-1.5">
                  📑 {isKo ? '리포트' : 'Report'}
                </span>
                {filesModalTab === 'report' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-status-go" />
                )}
              </button>
            </div>

            {/* 파일 목록 */}
            <div className="space-y-2 mb-4 max-h-64 overflow-y-auto min-h-[120px]">
              {/* 기획서 탭 */}
              {filesModalTab === 'planning' && (
                <>
                  {!isCurrentlyMaxOrTeam ? (
                    <div className="flex flex-col items-center justify-center py-8 text-terminal-muted">
                      <span className="text-3xl mb-2">🔒</span>
                      <p className="text-sm font-medium">{isKo ? 'MAX 플랜 전용 기능' : 'MAX Plan Feature'}</p>
                      <p className="text-xs mt-1 text-terminal-muted/70">
                        {isKo ? 'MAX 플랜에서 첨부한 기획서를 확인할 수 있습니다' : 'View attached planning documents with MAX plan'}
                      </p>
                    </div>
                  ) : detail.has_planning_files && detail.planning_file_names.length > 0 ? (
                    detail.planning_file_names.map((name, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-terminal-dim/20 rounded-lg border border-terminal-border">
                        <div 
                          className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => handlePreviewPlanningFile(idx, name)}
                          title={isKo ? '클릭하여 미리보기' : 'Click to preview'}
                        >
                          <span className="text-lg">📄</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-terminal-text truncate hover:text-status-go transition-colors">
                              {name}
                            </p>
                            <p className="text-xs text-terminal-muted">
                              {isKo ? '기획 문서' : 'Planning document'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {/* 미리보기 버튼 (메인) */}
                          <button
                            onClick={() => handlePreviewPlanningFile(idx, name)}
                            className="p-2 hover:bg-purple-500/20 rounded-lg text-purple-400 flex-shrink-0"
                            title={isKo ? '미리보기' : 'Preview'}
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          {/* 다운로드 버튼 (보조) */}
                          <button
                            onClick={() => handleDownloadPlanningFile(idx)}
                            disabled={downloadingPlanningIndex !== null}
                            className="p-2 hover:bg-status-go/20 rounded-lg text-terminal-muted hover:text-status-go disabled:opacity-50 flex-shrink-0"
                            title={isKo ? '다운로드' : 'Download'}
                          >
                            {downloadingPlanningIndex === idx ? (
                              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                            ) : (
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-terminal-muted">
                      <svg className="w-12 h-12 mb-2 opacity-30" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="12" y="8" width="40" height="48" rx="4" stroke="currentColor" strokeWidth="2"/>
                        <path d="M22 24H42M22 32H38M22 40H40" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
                      </svg>
                      <p className="text-sm">{isKo ? '첨부된 기획서가 없습니다' : 'No planning files attached'}</p>
                    </div>
                  )}
                </>
              )}

              {/* 테스트케이스 탭 */}
              {filesModalTab === 'testcase' && (
                <>
                  {detail.has_testcase_file ? (
                    <div className="flex items-center justify-between p-3 bg-terminal-dim/20 rounded-lg border border-terminal-border">
                      <div 
                        className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => handlePreviewTestcaseFile(detail.testcase_file_name || 'testcase.csv')}
                        title={isKo ? '클릭하여 미리보기' : 'Click to preview'}
                      >
                        <span className="text-lg">📊</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-terminal-text truncate hover:text-status-go transition-colors">
                            {detail.testcase_file_name || (isKo ? '테스트케이스 파일' : 'Testcase file')}
                            <span className="ml-1.5 text-xs text-terminal-muted opacity-60">👁️</span>
                          </p>
                          <p className="text-xs text-terminal-muted">
                            {isKo ? '테스트케이스 • 클릭하여 미리보기' : 'Testcase • Click to preview'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleDownloadTestcase}
                        disabled={isDownloadingTestcase}
                        className="p-2 hover:bg-status-go/20 rounded-lg text-status-go disabled:opacity-50 flex-shrink-0"
                        title={isKo ? '다운로드' : 'Download'}
                      >
                        {isDownloadingTestcase ? (
                          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-terminal-muted">
                      <svg className="w-12 h-12 mb-2 opacity-30" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="8" y="12" width="48" height="40" rx="4" stroke="currentColor" strokeWidth="2"/>
                        <path d="M8 24H56" stroke="currentColor" strokeWidth="2"/>
                        <path d="M20 12V24" stroke="currentColor" strokeWidth="2"/>
                        <path d="M32 12V24" stroke="currentColor" strokeWidth="2"/>
                        <path d="M44 12V24" stroke="currentColor" strokeWidth="2"/>
                        <circle cx="32" cy="38" r="2" fill="currentColor" opacity="0.5"/>
                      </svg>
                      <p className="text-sm">{isKo ? '첨부된 테스트케이스가 없습니다' : 'No testcase files attached'}</p>
                    </div>
                  )}
                </>
              )}

              {/* 리포트 탭 */}
              {filesModalTab === 'report' && (
                <div className="flex items-center justify-between p-3 bg-terminal-dim/20 rounded-lg border border-terminal-border">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-lg">📑</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-terminal-text truncate">
                        {detail.feature_name}_Report.pdf
                      </p>
                      <p className="text-xs text-terminal-muted">
                        {isKo ? 'PDF 리포트' : 'PDF Report'}
                        {!canDownloadPdf && (
                          <span className="ml-2 text-purple-400">
                            ({isKo ? '뷰어만 가능' : 'Viewer only'})
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* 뷰어 버튼 (모든 플랜) */}
                    <button
                      onClick={() => {
                        setShowFilesModal(false);
                        handleOpenPdfViewer();
                      }}
                      disabled={isPdfLoading}
                      className="p-2 hover:bg-purple-500/20 rounded-lg text-purple-400 disabled:opacity-50 flex-shrink-0"
                      title={isKo ? '리포트 보기' : 'View Report'}
                    >
                      {isPdfLoading ? (
                        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* 전체 다운로드 버튼 */}
            {(detail.has_testcase_file || detail.has_planning_files) && (
              <div className="pt-4 border-t border-terminal-border">
                <button
                  onClick={handleDownloadAllFilesZip}
                  disabled={isDownloadingZip}
                  className="w-full px-4 py-3 bg-status-go text-white rounded-lg hover:bg-status-go/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isDownloadingZip ? (
                    <>
                      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {isKo ? '압축 중...' : 'Creating ZIP...'}
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {isKo ? '전체 다운로드 (ZIP)' : 'Download All (ZIP)'}
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* PDF 뷰어 모달 */}
      {showPdfViewer && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={handleClosePdfViewer}
        >
          <div
            className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 헤더 */}
            <div className="flex-shrink-0 border-b border-terminal-border px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-lg">📄</span>
                <h3 className="text-base font-semibold text-terminal-text">
                  {isKo ? 'PDF 리포트' : 'PDF Report'}
                </h3>
                {/* 워터마크 안내 (Free/Pro) */}
                {quotaInfo && (quotaInfo.plan === 'free' || quotaInfo.plan === 'pro') && (
                  <span className="text-xs text-terminal-muted bg-terminal-dim/30 px-2 py-1 rounded">
                    {isKo ? '워터마크 포함' : 'Includes watermark'}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* 다운로드 버튼 (Pro 이상) */}
                {canDownloadPdf && (
                  <button
                    onClick={handleDownloadPdf}
                    className="px-4 py-2 bg-status-go text-white rounded-lg hover:bg-status-go/90 transition-colors flex items-center gap-2 text-sm font-medium"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    {isKo ? '다운로드' : 'Download'}
                  </button>
                )}
                {/* 닫기 버튼 */}
                <button
                  onClick={handleClosePdfViewer}
                  className="p-2 hover:bg-terminal-dim/30 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-terminal-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* PDF 뷰어 */}
            <div className="flex-1 overflow-hidden p-4">
              {isPdfLoading ? (
                <div className="w-full h-full flex items-center justify-center rounded-lg border border-terminal-border bg-terminal-dim/10">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-3 border-status-go/30 border-t-status-go rounded-full animate-spin"></div>
                    <p className="text-terminal-muted text-sm">
                      {isKo ? 'PDF를 불러오는 중...' : 'Loading PDF...'}
                    </p>
                  </div>
                </div>
              ) : pdfBlobUrl ? (
                <iframe
                  src={pdfBlobUrl}
                  className="w-full h-full rounded-lg border border-terminal-border"
                  title="PDF Report"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center rounded-lg border border-terminal-border bg-terminal-dim/10">
                  <p className="text-terminal-muted text-sm">
                    {isKo ? 'PDF를 로드할 수 없습니다.' : 'Failed to load PDF.'}
                  </p>
                </div>
              )}
            </div>

            {/* 푸터 - Free 플랜 업그레이드 안내 */}
            {!canDownloadPdf && (
              <div className="flex-shrink-0 border-t border-terminal-border px-5 py-3 bg-terminal-dim/20">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-terminal-muted">
                    {isKo 
                      ? '⭐ Pro 이상 플랜에서 PDF 다운로드가 가능합니다' 
                      : '⭐ PDF download available on Pro plan or higher'}
                  </p>
                  <button
                    onClick={() => {
                      handleClosePdfViewer();
                      onOpenPricing?.();
                    }}
                    className="px-4 py-1.5 text-sm bg-status-go text-white rounded-lg hover:bg-status-go/90 transition-colors"
                  >
                    {isKo ? '업그레이드' : 'Upgrade'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 파일 미리보기 모달 */}
      {previewModal.isOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={closePreviewModal}
        >
          <div
            className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl flex flex-col overflow-hidden w-[95vw] h-[90vh] max-w-[1400px]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 미리보기 헤더 */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-terminal-border bg-terminal-dim/20">
              <div className="flex items-center gap-3">
                <span className="text-xl">
                  {previewModal.fileType === 'csv' ? '📊' :
                   previewModal.fileType === 'text' ? '📄' :
                   previewModal.fileType === 'pdf' ? '📑' :
                   previewModal.fileType === 'image' ? '🖼️' : '📁'}
                </span>
                <div>
                  <h3 className="text-lg font-bold text-terminal-text truncate max-w-[60vw]">
                    {previewModal.fileName}
                  </h3>
                  <p className="text-xs text-terminal-muted">
                    {isKo ? '미리보기' : 'Preview'}
                  </p>
                </div>
              </div>
              <button
                onClick={closePreviewModal}
                className="p-2 hover:bg-terminal-dim/50 rounded-lg transition-colors text-terminal-muted hover:text-terminal-text"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* 미리보기 콘텐츠 */}
            <div className="flex-1 overflow-auto p-4">
              {previewModal.isLoading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-10 h-10 border-3 border-status-go/30 border-t-status-go rounded-full animate-spin"></div>
                    <p className="text-terminal-muted text-sm">
                      {isKo ? '파일을 불러오는 중...' : 'Loading file...'}
                    </p>
                  </div>
                </div>
              ) : previewModal.fileType === 'csv' && previewModal.content ? (
                /* CSV 테이블 뷰 */
                <div className="overflow-auto h-full">
                  <table className="min-w-full border-collapse text-sm">
                    <thead className="sticky top-0 bg-terminal-bg z-10">
                      {parseCsv(previewModal.content).slice(0, 1).map((row, rowIdx) => (
                        <tr key={rowIdx}>
                          {row.map((cell, cellIdx) => (
                            <th 
                              key={cellIdx}
                              className="px-3 py-2 text-left font-semibold text-terminal-text bg-terminal-dim/40 border border-terminal-border whitespace-nowrap"
                            >
                              {cell}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {parseCsv(previewModal.content).slice(1).map((row, rowIdx) => (
                        <tr key={rowIdx} className="hover:bg-terminal-dim/20">
                          {row.map((cell, cellIdx) => (
                            <td 
                              key={cellIdx}
                              className="px-3 py-2 text-terminal-text border border-terminal-border max-w-[400px] truncate"
                              title={cell}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-4 text-center text-xs text-terminal-muted">
                    {isKo 
                      ? `총 ${parseCsv(previewModal.content).length - 1}개 행`
                      : `Total ${parseCsv(previewModal.content).length - 1} rows`
                    }
                  </div>
                </div>
              ) : previewModal.fileType === 'text' && previewModal.content ? (
                /* 텍스트/마크다운 뷰 */
                <div className="h-full">
                  <pre className="p-4 bg-terminal-dim/20 rounded-lg border border-terminal-border text-terminal-text text-sm font-mono whitespace-pre-wrap overflow-auto h-full">
                    {previewModal.content}
                  </pre>
                </div>
              ) : previewModal.fileType === 'pdf' && previewModal.blobUrl ? (
                /* PDF 뷰 */
                <div className="h-full flex flex-col">
                  <object
                    data={previewModal.blobUrl}
                    type="application/pdf"
                    className="flex-1 w-full rounded-lg border border-terminal-border"
                  >
                    <div className="h-full flex items-center justify-center text-terminal-muted">
                      <div className="text-center">
                        <p className="mb-4">
                          {isKo ? 'PDF를 표시할 수 없습니다.' : 'Unable to display PDF.'}
                        </p>
                        <a
                          href={previewModal.blobUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-status-go text-white rounded-lg hover:bg-status-go/90"
                        >
                          {isKo ? '새 탭에서 열기' : 'Open in new tab'}
                        </a>
                      </div>
                    </div>
                  </object>
                </div>
              ) : previewModal.fileType === 'image' && previewModal.blobUrl ? (
                /* 이미지 뷰 */
                <div className="h-full flex items-center justify-center p-4">
                  <img
                    src={previewModal.blobUrl}
                    alt={previewModal.fileName}
                    className="max-w-full max-h-full object-contain rounded-lg border border-terminal-border shadow-lg"
                  />
                </div>
              ) : (
                /* 미리보기 불가 */
                <div className="h-full flex items-center justify-center text-terminal-muted">
                  <div className="text-center">
                    <svg className="w-16 h-16 mx-auto mb-4 opacity-30" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="12" y="8" width="40" height="48" rx="4" stroke="currentColor" strokeWidth="2"/>
                      <path d="M24 28L32 36L40 28" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M32 36V20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M20 44H44" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    <p>{isKo ? '미리보기를 지원하지 않는 파일 형식입니다.' : 'Preview is not supported for this file type.'}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

